import React, { useEffect, useState } from 'react';
import { useHelp } from '../contexts/HelpContext';
import { BASICS, GLOSSARY, WELCOME_TOUR, getPageHelp } from '../help/content';
import Tour from './Tour';

// The "?" in the top bar. A dot marks pages whose tour hasn't been seen yet.
export function HelpButton() {
  const { openHelp, page, hasTour, isSeen } = useHelp();
  const unseen = hasTour(page) && !isSeen(page);
  return (
    <button
      className={`help-btn ${unseen ? 'nudge' : ''}`}
      data-tour="help"
      onClick={openHelp}
      title="Help for this page (?)"
      aria-label="Help for this page"
    >
      ?
    </button>
  );
}

function Section({ title, children }) {
  return (
    <div className="help-section">
      <h4>{title}</h4>
      {children}
    </div>
  );
}

// Drawer + tour overlay. Rendered once, from the shell.
export function HelpLayer({ pageTitle }) {
  const {
    page, helpOpen, closeHelp,
    tourId, startTour, endTour, hasTour,
    autoTour, setAutoTour, resetTours,
  } = useHelp();
  const [basicsOpen, setBasicsOpen] = useState(false);
  const [didReset, setDidReset] = useState(false);
  useEffect(() => { if (!helpOpen) setDidReset(false); }, [helpOpen]);

  const help = getPageHelp(page);
  const tour = tourId === 'welcome' ? WELCOME_TOUR : getPageHelp(tourId)?.tour;

  const terms = (help?.metrics || [])
    .map(m => (Array.isArray(m) ? m : GLOSSARY[m]))
    .filter(Boolean);

  return (
    <>
      {tour && (
        <Tour
          key={tourId}
          steps={tour.steps}
          label={tourId === 'welcome' ? 'Welcome' : pageTitle}
          onClose={endTour}
        />
      )}

      {helpOpen && (
        <>
          <div className="help-scrim" onClick={closeHelp} />
          <aside className="help-drawer" role="dialog" aria-label={`Help — ${pageTitle}`}>
            <div className="help-head">
              <div>
                <div className="help-eyebrow">How to read</div>
                <h3>{pageTitle}</h3>
              </div>
              <button className="tour-x" onClick={closeHelp} aria-label="Close help">×</button>
            </div>

            <div className="help-body">
              {help?.purpose && <p className="help-lede">{help.purpose}</p>}

              <div className="help-actions">
                {hasTour(page) && (
                  <button className="btn-primary inline" onClick={() => startTour(page)}>
                    ▶ Take the tour of this page
                  </button>
                )}
                <button className="btn-ghost" onClick={() => startTour('welcome')}>
                  Replay the welcome tour
                </button>
              </div>

              {!help && (
                <p className="help-empty">
                  There's no written guide for this page yet. The welcome tour above still
                  covers the filters, comparison basis and store picker that drive it.
                </p>
              )}

              {help?.read?.length > 0 && (
                <Section title="What's on this page">
                  <dl className="help-list">
                    {help.read.map(([h, p]) => (
                      <React.Fragment key={h}>
                        <dt>{h}</dt>
                        <dd>{p}</dd>
                      </React.Fragment>
                    ))}
                  </dl>
                </Section>
              )}

              {terms.length > 0 && (
                <Section title="What the numbers mean">
                  <dl className="help-list">
                    {terms.map(([t, d]) => (
                      <React.Fragment key={t}>
                        <dt>{t}</dt>
                        <dd>{d}</dd>
                      </React.Fragment>
                    ))}
                  </dl>
                </Section>
              )}

              {help?.watch?.length > 0 && (
                <Section title="Watch out for">
                  <ul className="help-warn">
                    {help.watch.map(w => <li key={w}>{w}</li>)}
                  </ul>
                </Section>
              )}

              <div className="help-section">
                <button className="help-toggle" onClick={() => setBasicsOpen(o => !o)}>
                  {basicsOpen ? '▾' : '▸'} Data basics — applies to every page
                </button>
                {basicsOpen && (
                  <dl className="help-list">
                    {BASICS.map(([h, p]) => (
                      <React.Fragment key={h}>
                        <dt>{h}</dt>
                        <dd>{p}</dd>
                      </React.Fragment>
                    ))}
                  </dl>
                )}
              </div>
            </div>

            <div className="help-foot">
              <label className="help-check">
                <input
                  type="checkbox"
                  checked={autoTour}
                  onChange={e => setAutoTour(e.target.checked)}
                />
                Show a page's tour the first time I open it
              </label>
              <button
                className="btn-ghost"
                onClick={() => { resetTours(); setDidReset(true); }}
              >
                {didReset ? '✓ Tours reset' : 'Reset all tours'}
              </button>
            </div>
          </aside>
        </>
      )}
    </>
  );
}
